/**
 * Usage tab of the Narration page (Backlog 063): the NarrationGenerations ledger by month, by user and by
 * adventure/Story, with a per-user drill-down. Users are shown by display name only.
 */

import { useMemo, useState, type ReactNode } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { DataTable, type DataTableColumn } from '@components/common/DataTable'
import { SearchField } from '@components/common/SearchField'
import { useMobileDialogProps } from '@components/common/useMobileDialogProps'
import { narrationApi } from '@api/endpoints/narration'
import type {
  NarrationGenerationRow,
  NarrationUsageMonth,
  NarrationUsageTarget,
  NarrationUsageTotals,
  NarrationUsageUser,
} from '@api/types/narration'
import { narrationErrorMessage } from './narrationForm'
import {
  ALL_TIME,
  allowanceFraction,
  allowanceLabel,
  generationKindLabel,
  monthOptions,
  monthParam,
  savingsLabel,
  shortMonthLabel,
  statusChip,
  targetKindLabel,
  targetTitle,
  toMonthKey,
  trendMax,
  usageKey,
} from './narrationUsage'

const n = (value: number) => value.toLocaleString('en-US')
const when = (iso: string) => new Date(iso).toLocaleString()

export function NarrationUsageTab() {
  const options = useMemo(() => monthOptions(new Date()), [])
  const [monthValue, setMonthValue] = useState(() => toMonthKey(new Date()))
  const month = monthParam(monthValue)
  const [drill, setDrill] = useState<{ userId: string; displayName: string } | null>(null)

  const summary = useQuery({
    queryKey: usageKey('summary', { month }),
    queryFn: () => narrationApi.usage.summary({ month }),
  })

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <TextField
          select
          size="small"
          label="Month (UTC)"
          value={monthValue}
          onChange={(e) => setMonthValue(e.target.value)}
          sx={{ minWidth: 240 }}
        >
          {options.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
          <MenuItem value={ALL_TIME}>All time</MenuItem>
        </TextField>
        <Typography variant="caption" color="text.secondary">
          Billed counts generated narrations and previews. Saved is what reusing identical audio did not bill.
        </Typography>
      </Stack>

      {summary.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {narrationErrorMessage(summary.error, 'Failed to load narration usage')}
        </Alert>
      )}
      {summary.isLoading && <LinearProgress sx={{ mb: 2 }} />}
      {summary.data && (
        <>
          <TotalsCards totals={summary.data.totals} />
          <Section title={`Last 12 months${month ? ` to ${shortMonthLabel(month)}` : ''}`}>
            <Trend months={summary.data.months} />
          </Section>
        </>
      )}

      <Section title="By user">
        <UsersTable month={month} onPick={(u) => setDrill({ userId: u.userId, displayName: u.displayName })} />
      </Section>

      <Section title="By adventure or Story">
        <TargetsTable month={month} />
      </Section>

      {drill && <UserDrillDown month={month} user={drill} onClose={() => setDrill(null)} />}
    </Box>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        {title}
      </Typography>
      {children}
    </Box>
  )
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="h6" sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.3 }}>
        {value}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      )}
    </Paper>
  )
}

function TotalsCards({ totals }: { totals: NarrationUsageTotals }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))', md: 'repeat(6, minmax(0, 1fr))' },
        gap: 1,
        mb: 3,
      }}
    >
      <Stat label="Requests" value={n(totals.requests)} hint={`${n(totals.previews)} previews`} />
      <Stat label="Characters billed" value={n(totals.charactersBilled)} hint={`of ${n(totals.charactersRequested)} requested`} />
      <Stat
        label="Saved by reuse"
        value={n(totals.charactersSaved)}
        hint={`${savingsLabel(totals.charactersSaved, totals.charactersRequested)} of requested`}
      />
      <Stat label="Users" value={n(totals.users)} />
      <Stat label="Generated / reused" value={`${n(totals.succeeded)} / ${n(totals.reused)}`} />
      <Stat label="Failed / pending" value={`${n(totals.failed)} / ${n(totals.pending)}`} />
    </Box>
  )
}

function Trend({ months }: { months: NarrationUsageMonth[] }) {
  const max = trendMax(months.map((m) => m.charactersBilled))
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      {months.map((m) => (
        <Box
          key={m.month}
          sx={{ display: 'grid', gridTemplateColumns: '56px minmax(0, 1fr) auto', alignItems: 'center', columnGap: 1, py: 0.25 }}
        >
          <Typography variant="caption" color="text.secondary">
            {shortMonthLabel(m.month)}
          </Typography>
          <Box sx={{ height: 10, borderRadius: 1, bgcolor: 'action.hover', overflow: 'hidden' }}>
            <Box sx={{ height: '100%', width: `${(m.charactersBilled / max) * 100}%`, bgcolor: 'primary.main' }} />
          </Box>
          <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums', textAlign: 'right', minWidth: 72 }}>
            {n(m.charactersBilled)}
          </Typography>
          {/* The month's detail as text under the bar (it used to be a hover tooltip, which a
              phone never shows). Skipped for empty months so the trend stays compact. */}
          {m.requests > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ gridColumn: '2 / 4', mt: -0.25, lineHeight: 1.3 }}>
              {n(m.requests)} requests · {n(m.users)} users · {n(m.charactersSaved)} saved by reuse
            </Typography>
          )}
        </Box>
      ))}
    </Paper>
  )
}

function useServerPage(initialSize = 25) {
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(initialSize)
  return {
    page,
    pageSize,
    setPage,
    setPageSize: (size: number) => {
      setPageSize(size)
      setPage(0)
    },
    reset: () => setPage(0),
  }
}

function UsersTable({ month, onPick }: { month: string | null; onPick: (u: NarrationUsageUser) => void }) {
  const paging = useServerPage()
  const [lastMonth, setLastMonth] = useState(month)
  if (lastMonth !== month) {
    setLastMonth(month)
    paging.reset()
  }
  const [search, setSearch] = useState('')
  const q = { month, search: search || null, page: paging.page + 1, pageSize: paging.pageSize }
  const query = useQuery({
    queryKey: usageKey('users', q),
    queryFn: () => narrationApi.usage.users(q),
    placeholderData: keepPreviousData,
  })

  const columns = useMemo<DataTableColumn<NarrationUsageUser>[]>(
    () => [
      { key: 'user', header: 'User', primary: true, render: (u) => <Typography sx={{ fontWeight: 600 }}>{u.displayName}</Typography> },
      { key: 'billed', header: 'Billed', align: 'right', render: (u) => n(u.charactersBilled) },
      { key: 'saved', header: 'Saved', align: 'right', hideBelow: 'sm', render: (u) => n(u.charactersSaved) },
      { key: 'requests', header: 'Requests', align: 'right', render: (u) => n(u.requests) },
      { key: 'failed', header: 'Failed', align: 'right', hideBelow: 'md', render: (u) => n(u.failed) },
      { key: 'last', header: 'Last request', hideBelow: 'md', render: (u) => when(u.lastRequestAt) },
    ],
    []
  )

  return (
    <DataTable<NarrationUsageUser>
      rows={query.data?.items ?? []}
      columns={columns}
      getRowKey={(u) => u.userId}
      loading={query.isLoading}
      error={query.error ? narrationErrorMessage(query.error, 'Failed to load users') : null}
      onRetry={() => query.refetch()}
      emptyMessage={search ? 'No users match that search in this period.' : 'No narration requests in this period.'}
      page={paging.page}
      pageSize={paging.pageSize}
      total={query.data?.totalCount ?? 0}
      onPageChange={paging.setPage}
      onPageSizeChange={paging.setPageSize}
      onRowClick={onPick}
      toolbar={
        <SearchField
          value={search}
          onSearch={(next) => {
            setSearch(next)
            paging.reset()
          }}
          placeholder="Search by name or email"
        />
      }
    />
  )
}

function TargetsTable({ month, userId }: { month: string | null; userId?: string }) {
  const paging = useServerPage(userId ? 10 : 25)
  const [lastMonth, setLastMonth] = useState(month)
  if (lastMonth !== month) {
    setLastMonth(month)
    paging.reset()
  }
  const q = { month, userId, page: paging.page + 1, pageSize: paging.pageSize }
  const query = useQuery({
    queryKey: usageKey('targets', q),
    queryFn: () => narrationApi.usage.targets(q),
    placeholderData: keepPreviousData,
  })

  const columns = useMemo<DataTableColumn<NarrationUsageTarget>[]>(
    () => [
      {
        key: 'title',
        header: 'Adventure / Story',
        primary: true,
        render: (t) => (
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, wordBreak: 'break-word' }} color={t.title || t.kind === 'preview' ? undefined : 'text.secondary'}>
              {targetTitle(t.kind, t.title)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {targetKindLabel(t.kind)}
            </Typography>
          </Box>
        ),
      },
      { key: 'billed', header: 'Billed', align: 'right', render: (t) => n(t.charactersBilled) },
      { key: 'saved', header: 'Saved', align: 'right', hideBelow: 'sm', render: (t) => n(t.charactersSaved) },
      { key: 'requests', header: 'Requests', align: 'right', render: (t) => n(t.requests) },
      { key: 'users', header: 'Users', align: 'right', hideBelow: 'md', render: (t) => n(t.users) },
    ],
    []
  )

  return (
    <DataTable<NarrationUsageTarget>
      rows={query.data?.items ?? []}
      columns={columns}
      getRowKey={(t) => `${t.kind}:${t.id ?? ''}`}
      loading={query.isLoading}
      error={query.error ? narrationErrorMessage(query.error, 'Failed to load adventures and Stories') : null}
      emptyMessage="Nothing narrated in this period."
      page={paging.page}
      pageSize={paging.pageSize}
      total={query.data?.totalCount ?? 0}
      onPageChange={paging.setPage}
      onPageSizeChange={paging.setPageSize}
      dense={!!userId}
    />
  )
}

function GenerationsTable({ month, userId }: { month: string | null; userId: string }) {
  const paging = useServerPage(10)
  const q = { month, userId, page: paging.page + 1, pageSize: paging.pageSize }
  const query = useQuery({
    queryKey: usageKey('generations', q),
    queryFn: () => narrationApi.usage.generations(q),
    placeholderData: keepPreviousData,
  })

  const columns = useMemo<DataTableColumn<NarrationGenerationRow>[]>(
    () => [
      {
        key: 'when',
        header: 'When',
        primary: true,
        render: (g) => (
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2">{when(g.createdAt)}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
              {generationKindLabel(g.targetKind)}
              {g.adventureTitle ? ` · ${g.adventureTitle}` : g.storyTitle ? ` · ${g.storyTitle}` : ''}
            </Typography>
          </Box>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        render: (g) => {
          const chip = statusChip(g.status)
          return (
            <Box sx={{ minWidth: 0 }}>
              <Chip size="small" variant="outlined" color={chip.color} label={chip.label} />
              {g.error && (
                <Typography variant="caption" color="error" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
                  {g.error}
                </Typography>
              )}
            </Box>
          )
        },
      },
      { key: 'chars', header: 'Characters', align: 'right', render: (g) => n(g.characterCount) },
      { key: 'billed', header: 'Billed', align: 'right', render: (g) => n(g.charactersBilled) },
      {
        key: 'voice',
        header: 'Voice',
        hideBelow: 'md',
        render: (g) => `${g.voiceDisplayName ?? 'Deleted voice'} · ${g.language} · ${g.modelId}`,
      },
    ],
    []
  )

  return (
    <DataTable<NarrationGenerationRow>
      rows={query.data?.items ?? []}
      columns={columns}
      getRowKey={(g) => g.id}
      loading={query.isLoading}
      error={query.error ? narrationErrorMessage(query.error, 'Failed to load requests') : null}
      emptyMessage="No requests in this period."
      page={paging.page}
      pageSize={paging.pageSize}
      total={query.data?.totalCount ?? 0}
      onPageChange={paging.setPage}
      onPageSizeChange={paging.setPageSize}
      dense
    />
  )
}

function UserDrillDown({
  month,
  user,
  onClose,
}: {
  month: string | null
  user: { userId: string; displayName: string }
  onClose: () => void
}) {
  const dialogProps = useMobileDialogProps()
  const summary = useQuery({
    queryKey: usageKey('summary', { month, userId: user.userId }),
    queryFn: () => narrationApi.usage.summary({ month, userId: user.userId }),
  })
  const data = summary.data
  const fraction = data ? allowanceFraction(data.userUsedThisMonth ?? 0, data.userMonthlyAllowance) : null

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth {...dialogProps}>
      <DialogTitle>{data?.userDisplayName ?? user.displayName}</DialogTitle>
      <DialogContent dividers>
        {summary.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {narrationErrorMessage(summary.error, 'Failed to load this user')}
          </Alert>
        )}
        {summary.isLoading && <LinearProgress sx={{ mb: 2 }} />}
        {data && (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2">
                This month: {allowanceLabel(data.userUsedThisMonth ?? 0, data.userMonthlyAllowance)}
                {!data.userMonthlyAllowance ? ' (no allowance on their current plan)' : ''}
              </Typography>
              {fraction != null && (
                <LinearProgress
                  variant="determinate"
                  value={fraction}
                  color={fraction >= 90 ? 'warning' : 'primary'}
                  sx={{ height: 6, borderRadius: 3, mt: 0.5, maxWidth: 360 }}
                />
              )}
            </Box>
            <TotalsCards totals={data.totals} />
          </>
        )}
        <Section title="Adventures and Stories">
          <TargetsTable month={month} userId={user.userId} />
        </Section>
        <Section title="Requests">
          <GenerationsTable month={month} userId={user.userId} />
        </Section>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
